'use client'

import { useRef, useState } from 'react'
import { format } from 'date-fns'
import { useTranslations, useLocale } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  AlertCircle,
  BriefcaseIcon,
  GlobeIcon,
  LockIcon,
  MailIcon,
  PhoneIcon,
  SaveIcon,
  UserIcon,
  XIcon,
  CalendarIcon
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getErrorMessage, setValidationErrors } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { PasswordInput } from '@/components/ui/password-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Combobox } from '@/components/ui/combobox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { DatePicker } from '@/components/ui/date-picker'
import { useMeStore, useMetaStore } from '@/store'
import { UpdateProfileParams } from '@/types/user'
import { useScrollToError } from '@/hooks'
import { GenderEnum } from '@/enums'

type PropsType = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type FormData = {
  firstname: string
  lastname: string
  gender: GenderEnum
  birthDate: string
  email: string
  timezone: string
  currencyCode: string
  company: string
  phone: string
  password: string
  passwordConfirm: string
}

const UpdateProfileDialog = ({ open, onOpenChange }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const { meState, editProfile } = useMeStore()
  const { metaState } = useMetaStore()
  const user = meState.response
  const { scrollToFirstError } = useScrollToError()
  const formRef = useRef<HTMLFormElement>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const schema = z
    .object({
      firstname: z.string().min(1, t('not_blank')),
      lastname: z.string().min(1, t('not_blank')),
      gender: z.enum([GenderEnum.MALE, GenderEnum.FEMALE, GenderEnum.DIVERSE, GenderEnum.UNKNOWN]),
      birthDate: z.string().min(1, t('not_blank')),
      email: z.email(t('invalid_email')),
      timezone: z.string().min(1, t('not_blank')),
      currencyCode: z.string(),
      company: z.string(),
      phone: z.string(),
      password: z.string(),
      passwordConfirm: z.string()
    })
    .refine(
      data => {
        if (data.password && data.password.length > 0) {
          return data.password === data.passwordConfirm && data.password.length >= 8
        }
        return true
      },
      { message: t('password_mismatch'), path: ['passwordConfirm'] }
    )

  const form = useForm<FormData>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      gender: user?.gender || GenderEnum.UNKNOWN,
      birthDate: user?.birthDate || '',
      email: user?.email || '',
      timezone: user?.timezone || '',
      currencyCode: user?.currencyCode || '',
      company: user?.company || '',
      phone: user?.phone || '',
      password: '',
      passwordConfirm: ''
    }
  })

  const [isPending, setIsPending] = useState(false)

  const handleSubmit = async (data: FormData) => {
    const params: UpdateProfileParams = {
      firstname: data.firstname,
      lastname: data.lastname,
      gender: data.gender,
      birthDate: data.birthDate,
      email: data.email,
      timezone: data.timezone,
      currencyCode: data.currencyCode || undefined,
      company: data.company || undefined,
      phone: data.phone || undefined
    }

    if (data.password && data.password.length > 0) {
      params.password = data.password
      params.passwordConfirm = data.passwordConfirm
    }

    setIsPending(true)
    try {
      await editProfile(params)
      toast.success(t('updated_successfully'))
      setFormError(null)
      onOpenChange(false)
    } catch (error: any) {
      const message = getErrorMessage(error, t('something_went_wrong'))
      if (error.response?.status === 422) {
        setValidationErrors(error, form.setError)
        setFormError(null)
        scrollToFirstError()
      } else {
        setFormError(message)
        toast.error(message)
      }
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden p-0" aria-describedby={undefined}>
        <div className="flex flex-col p-6" style={{ maxHeight: 'min(90dvh, calc(100dvh - 2rem))' }}>
          <DialogHeader className="mb-4 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              {t('edit_profile')}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              ref={formRef}
              onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)}
              className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-2 px-1 py-2 pr-5 pb-4">
                  {formError && (
                    <Alert variant="destructive" onClose={() => setFormError(null)}>
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>{t('error')}</AlertTitle>
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  )}
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="firstname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('firstname')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('enter_firstname')} {...field} autoFocus />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('lastname')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('enter_lastname')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-1">
                            <BriefcaseIcon className="h-4 w-4" />
                            {t('company')}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={t('enter_company')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-1">
                            <PhoneIcon className="h-4 w-4" />
                            {t('phone')}
                          </FormLabel>
                          <FormControl>
                            <PhoneInput international value={field.value} onChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required className="flex items-center gap-1">
                            <MailIcon className="h-4 w-4" />
                            {t('email')}
                          </FormLabel>
                          <FormControl>
                            <Input type="email" placeholder={t('enter_email')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('gender')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl className="w-full">
                              <SelectTrigger>
                                <SelectValue placeholder={t('select')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(GenderEnum).map(gender => (
                                <SelectItem key={gender} value={gender}>
                                  {t(gender)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="birthDate"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-1">
                            <CalendarIcon className="h-4 w-4" />
                            {t('birth_date')}
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value ? new Date(field.value) : undefined}
                              onChange={date => field.onChange(date ? format(date, 'yyyy-MM-dd') : undefined)}
                              placeholder={t('select_date')}
                              isInvalid={!!fieldState.error}
                              locale={locale}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="timezone"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel required className="flex items-center gap-1">
                            <GlobeIcon className="h-4 w-4" />
                            {t('timezone')}
                          </FormLabel>
                          <FormControl>
                            <Combobox
                              options={(metaState.response?.timezones || []).map(tz => ({
                                label: tz.id,
                                value: tz.id,
                                description: tz.offset,
                                searchValue: `${tz.id} ${tz.offset}`
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              icon={<GlobeIcon className="h-4 w-4" />}
                              placeholder={t('select')}
                              searchPlaceholder={t('search')}
                              emptyText={t('not_found')}
                              clearable={false}
                              error={!!fieldState.error}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="currencyCode"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t('currency')}</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl className="w-full">
                              <SelectTrigger className={fieldState.error ? 'border-destructive' : ''}>
                                <SelectValue placeholder={t('select')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {(metaState.response?.currencies || []).map(currency => (
                                <SelectItem key={currency.code} value={currency.code}>
                                  {currency.symbol} {currency.code} — {currency.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator className="mb-8" />

                  {/* Change Password */}
                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-base font-medium">
                      <LockIcon className="h-4 w-4" />
                      {t('change_password')}
                      <span className="text-muted-foreground text-sm font-normal">({t('optional')})</span>
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('password')}</FormLabel>
                            <FormControl>
                              <PasswordInput placeholder={t('enter_new_password')} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="passwordConfirm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('password_confirm')}</FormLabel>
                            <FormControl>
                              <PasswordInput placeholder={t('confirm_password')} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <DialogFooter className="mt-4 shrink-0">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                  <XIcon className="h-4 w-4" />
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={isPending}>
                  <SaveIcon className="h-4 w-4" />
                  {isPending ? `${t('loading')}...` : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateProfileDialog
